/**
 * Betting calculation utilities
 * Implements the betting formula from the requirements
 */

/**
 * Calculate coefficient for a bet
 * Matches the backend place-bet function calculation:
 * 1) Add bet amount to chosen side's pool
 * 2) Calculate coefficient based on pools AFTER adding the bet
 * @param {string} side - 'yes' or 'no'
 * @param {number} betAmount - Amount being bet (X)
 * @param {number} poolYes - Current pool for 'yes' side
 * @param {number} poolNo - Current pool for 'no' side
 * @returns {number} - The coefficient
 */
export function calculateCoefficient(side, betAmount, poolYes, poolNo) {
  // Step 1: Add the bet amount to the chosen side's pool
  let updatedPoolYes = poolYes;
  let updatedPoolNo = poolNo;
  if (side === 'yes') {
    updatedPoolYes += betAmount;
  } else {
    updatedPoolNo += betAmount;
  }
  
  // Step 2: Compute coefficient based on pools AFTER adding the bet
  // coef = (total_pool_after_bet + X) / (updated_pool_side + X)
  const totalPoolAfterBet = updatedPoolYes + updatedPoolNo;
  
  if (side === 'yes') {
    // coef_yes = (totalPoolAfterBet + X) / (updatedPoolYes + X)
    return (totalPoolAfterBet + betAmount) / (updatedPoolYes + betAmount);
  } else if (side === 'no') {
    // coef_no = (totalPoolAfterBet + X) / (updatedPoolNo + X)
    return (totalPoolAfterBet + betAmount) / (updatedPoolNo + betAmount);
  } else {
    throw new Error('Side must be "yes" or "no"');
  }
}

/**
 * Calculate payout for a bet
 * @param {number} betAmount - Amount being bet (X)
 * @param {number} coefficient - The coefficient
 * @returns {number} - The payout amount
 */
export function calculatePayout(betAmount, coefficient) {
  return betAmount * coefficient;
}

/**
 * Calculate new pool values after a bet is placed
 * Matches the backend place-bet function calculation:
 * 1) Add bet amount to chosen side
 * 2) Calculate payout and profit
 * 3) Subtract profit from opposite side
 * @param {string} side - 'yes' or 'no'
 * @param {number} betAmount - Amount being bet (X)
 * @param {number} payout - Calculated payout
 * @param {number} currentPoolYes - Current pool for 'yes' side
 * @param {number} currentPoolNo - Current pool for 'no' side
 * @returns {Object} - { pool_yes: number, pool_no: number }
 */
export function updatePools(side, betAmount, payout, currentPoolYes, currentPoolNo) {
  // Step 1: Add the bet amount to the chosen side's pool
  let updatedPoolYes = currentPoolYes;
  let updatedPoolNo = currentPoolNo;
  if (side === 'yes') {
    updatedPoolYes += betAmount;
  } else {
    updatedPoolNo += betAmount;
  }
  
  // Step 2: Calculate profit
  const profit = payout - betAmount;
  
  // Step 3: Subtract the profit from the opposite side's pool
  let newPoolYes = updatedPoolYes;
  let newPoolNo = updatedPoolNo;
  if (side === 'yes') {
    newPoolNo = updatedPoolNo - profit;
  } else {
    newPoolYes = updatedPoolYes - profit;
  }
  
  return {
    pool_yes: newPoolYes,
    pool_no: newPoolNo
  };
}

/**
 * Calculate all bet details in one function
 * @param {string} side - 'yes' or 'no'
 * @param {number} betAmount - Amount being bet (X)
 * @param {number} poolYes - Current pool for 'yes' side
 * @param {number} poolNo - Current pool for 'no' side
 * @returns {Object} - { coefficient, payout, newPools }
 */
export function calculateBetDetails(side, betAmount, poolYes, poolNo) {
  // Validate inputs
  if (betAmount <= 0) {
    throw new Error('Bet amount must be greater than 0');
  }
  if (poolYes < 0 || poolNo < 0) {
    throw new Error('Pools cannot be negative');
  }
  if (side !== 'yes' && side !== 'no') {
    throw new Error('Side must be "yes" or "no"');
  }

  // Calculate coefficient
  const coefficient = calculateCoefficient(side, betAmount, poolYes, poolNo);
  
  // Calculate payout
  const payout = calculatePayout(betAmount, coefficient);
  
  // Calculate new pools
  const newPools = updatePools(side, betAmount, payout, poolYes, poolNo);
  
  // Validate new pools are not negative
  if (newPools.pool_yes < 0 || newPools.pool_no < 0) {
    throw new Error('Bet would result in negative pool. Bet amount too large.');
  }

  return {
    coefficient: parseFloat(coefficient.toFixed(4)),
    payout: parseFloat(payout.toFixed(2)),
    newPools: {
      pool_yes: parseFloat(newPools.pool_yes.toFixed(2)),
      pool_no: parseFloat(newPools.pool_no.toFixed(2))
    }
  };
}

/**
 * Validate bet can be placed
 * @param {number} betAmount - Amount being bet
 * @param {number} userBalance - User's current balance
 * @param {number} minBet - Minimum bet amount (optional)
 * @param {number} maxBet - Maximum bet amount (optional)
 * @returns {Object} - { valid: boolean, error?: string }
 */
export function validateBet(betAmount, userBalance, minBet = 0.01, maxBet = null) {
  if (betAmount < minBet) {
    return { valid: false, error: `Minimum bet is $${minBet}` };
  }
  
  if (maxBet && betAmount > maxBet) {
    return { valid: false, error: `Maximum bet is $${maxBet}` };
  }
  
  if (betAmount > userBalance) {
    return { valid: false, error: 'Insufficient balance' };
  }
  
  return { valid: true };
}

/**
 * Calculate win/loss for user's bets based on winning team
 * @param {Array} userBets - Array of user's bet objects with side/team_number and payout
 * @param {string} winnerSide - 'yes' or 'no' (the winning team) - for 2-team games, 'yes' = team 1, 'no' = team 2
 * @returns {Object} - { won: boolean, payoutDifference: number, winningPayout: number, losingPayout: number, winningBetsCount: number, losingBetsCount: number }
 */
export function calculateWinLoss(userBets, winnerSide) {
  if (!userBets || userBets.length === 0) {
    return {
      won: false,
      payoutDifference: 0,
      winningPayout: 0,
      losingPayout: 0,
      winningBetsCount: 0,
      losingBetsCount: 0
    };
  }

  if (winnerSide !== 'yes' && winnerSide !== 'no') {
    throw new Error('Winner side must be "yes" or "no"');
  }

  // Convert winnerSide to team number for comparison
  const winnerTeamNumber = winnerSide === 'yes' ? 1 : 2;

  // Separate bets by team_number - convert old format (side) to team_number first
  const winningBets = userBets.filter(bet => {
    // Determine team_number: use team_number if available, otherwise convert from side
    let teamNum = bet.team_number;
    if (teamNum === undefined || teamNum === null) {
      // Old format: convert side to team_number
      teamNum = bet.side === 'yes' ? 1 : bet.side === 'no' ? 2 : null;
    }
    return teamNum === winnerTeamNumber;
  });
  
  const losingBets = userBets.filter(bet => {
    // Determine team_number: use team_number if available, otherwise convert from side
    let teamNum = bet.team_number;
    if (teamNum === undefined || teamNum === null) {
      // Old format: convert side to team_number
      teamNum = bet.side === 'yes' ? 1 : bet.side === 'no' ? 2 : null;
    }
    return teamNum !== winnerTeamNumber && teamNum !== null;
  });

  // Calculate total payouts
  const winningPayout = winningBets.reduce((sum, bet) => sum + parseFloat(bet.payout || 0), 0);
  const losingPayout = losingBets.reduce((sum, bet) => sum + parseFloat(bet.payout || 0), 0);

  // Calculate difference (positive = win, negative = lose)
  // payoutDifference = winningPayout - losingPayout
  const payoutDifference = winningPayout - losingPayout;

  return {
    won: payoutDifference > 0,
    payoutDifference: parseFloat(payoutDifference.toFixed(2)),
    winningPayout: parseFloat(winningPayout.toFixed(2)),
    losingPayout: parseFloat(losingPayout.toFixed(2)),
    winningBetsCount: winningBets.length,
    losingBetsCount: losingBets.length
  };
}

